import {
  useAllowedDropTypes,
  useIsDraggableElement,
} from '@ui/drag-and-drop/drag-and-drop.hook';
import Draggable from '@ui/drag-and-drop/Draggable';
import Droppable, { DropType } from '@ui/drag-and-drop/Droppable';
import { HideOutsideViewport } from '@ui/HideOutsideViewport';

// These should not be imported here. NestedElementItem should not know what list items contain, and should not know about the features
import ElementListItem from '@features/projects/lists/ElementListItem/ElementListItem';
import { ProjectListItem } from '@features/projects/lists/ProjectListItem';
import ProductListItem from '@features/products/ProductListItem';
// ---

import { useProjectSelectorDrop } from '@client/hooks/use-droppable.hook';
import { useIsSelectedElement } from '@client/hooks/use-elements.hooks';
import {
  isElementExpanded,
  setElementExpanded,
} from '@client/hooks/use-expand-elements.hooks';
import { useMoveElements } from '@client/hooks/use-move-elements.hooks';
import { useProjectNavigation } from '@client/hooks/use-router.hooks';
import { getSelectedVersion, useUIState } from '@client/store/ui';
import { ListItem } from '@mui/material';
import {
  isProjectFolder,
  isProjectInfo,
  isProjectInfoOrFolder,
} from '@shared/helpers/project-folder.helpers';
import {
  getElementById,
  isElement,
  isOneOfElements,
  isProductElement,
  isProductListElement,
} from '@shared/helpers/recursive-element.helpers';
import {
  IElement,
  IElementID,
  OneOfElements,
  OneOfListElements,
} from '@shared/models/project.model';
import React, { useCallback, useMemo } from 'react';

import ProductElementListItem from '@features/products/ProductElementListItem';
import { LIST_ITEM_HEIGHT } from './list.constants';
import { INestedListBase } from './NestedElementList';

type NestedElementItemProps<T extends OneOfListElements> = INestedListBase & {
  element: OneOfElements<T>;
};

type NestedElementItemContentProps<T extends OneOfListElements> = {
  element: OneOfElements<T>;
  indentation: number;
  isLastAdded: boolean;
};

const NestedElementItem = <T extends OneOfListElements>({
  element,
  indentation = 0,
  cullBeforeMount = false,
  rowHeight = LIST_ITEM_HEIGHT,
}: NestedElementItemProps<T>) => {
  const isSelected = useIsSelectedElement(
    isOneOfElements(element) ? element : undefined,
  );
  const { recentlyUndoRedoElementId, observedElementIds, addedElementId } =
    useUIState(
      'recentlyUndoRedoElementId',
      'observedElementIds',
      'addedElementId',
    );

  const isLastAdded = addedElementId === element.id;
  const isRecentlyChanged = !!(
    recentlyUndoRedoElementId && recentlyUndoRedoElementId === element?.id
  );
  const alwaysRender =
    observedElementIds[element?.id ?? ''] ||
    (isProjectFolder(element) && isLastAdded);

  return (
    <HideOutsideViewport
      key={`hide-outside-viewport-${element?.id}`}
      alwaysVisible={alwaysRender}
      cullBeforeMount={cullBeforeMount}
      height={rowHeight}
      scrollTo={isRecentlyChanged || isLastAdded || isSelected}
    >
      <NestedElementItemContent
        element={element}
        indentation={indentation}
        isLastAdded={isLastAdded}
      />
    </HideOutsideViewport>
  );
};

/**
 * Row body mounted only while the placeholder is in view (or alwaysVisible).
 */
const NestedElementItemContent = <T extends OneOfListElements>({
  element,
  indentation,
  isLastAdded,
}: NestedElementItemContentProps<T>) => {
  const isProjectListItem = isProjectInfoOrFolder(element);
  const { moveElementsTo: moveElements, isMoveElementsAllowed } =
    useMoveElements();
  const { addObservedElementIds } = useUIState('addObservedElementIds');
  const draggable = useIsDraggableElement(element);
  const allowedDrops = useAllowedDropTypes(element);
  const navigateTo = useProjectNavigation();
  const onProjectDrop = useProjectSelectorDrop();
  const expanded = isElementExpanded(element);

  const handleElementDrop = useCallback(
    async (itemId: string, moveToId?: string | null, moveType?: DropType) => {
      if (!moveToId || !moveType) {
        throw new Error('No target element id');
      }

      await moveElements(moveToId, moveType, itemId);

      if (moveType === 'inside' && !expanded) {
        const version = getSelectedVersion();
        const parent = getElementById(version, moveToId);
        const elementsInParent =
          (parent as IElement | undefined)?.elements ?? [];

        addObservedElementIds(
          elementsInParent.reduce(
            (acc, { id }) =>
              ({
                ...acc,
                [id]: true,
              }) as Record<IElementID, boolean>,
            {},
          ) ?? {},
        );
        setElementExpanded(moveToId, true);
      }

      navigateTo({ elementId: itemId });
    },
    [moveElements, expanded, navigateTo, addObservedElementIds],
  );

  const validateDrop = useCallback(
    (itemId: string, moveToId?: string, moveType?: DropType) =>
      isMoveElementsAllowed(moveToId, moveType, itemId),
    [isMoveElementsAllowed],
  );

  const component = useMemo(() => {
    if (isProjectInfo(element) || isProjectFolder(element)) {
      return (
        <ProjectListItem
          item={element}
          indentation={indentation}
          autoFocus={isLastAdded}
        />
      );
    }
    if (isElement(element)) {
      return <ElementListItem element={element} indentation={indentation} />;
    }
    if (isProductElement(element)) {
      return (
        <ProductElementListItem element={element} indentation={indentation} />
      );
    }
    if (isProductListElement(element)) {
      return <ProductListItem item={element} indentation={indentation} />;
    }
    return <></>;
  }, [element, indentation, isLastAdded]);

  return (
    <Droppable
      onDrop={isProjectListItem ? onProjectDrop : handleElementDrop}
      allowDropInside={allowedDrops.inside}
      allowDropAbove={allowedDrops.above}
      allowDropBelow={allowedDrops.below}
      indentation={indentation}
      dropId={element?.id}
      validateFn={isProjectListItem ? SKIP_VALIDATION : validateDrop}
    >
      <Draggable dragId={element?.id} disabled={!draggable}>
        <ListItem disablePadding disableGutters>
          {component}
        </ListItem>
      </Draggable>
    </Droppable>
  );
};

// TODO: Should validate project list items
const SKIP_VALIDATION = () => true;

export default React.memo(NestedElementItem);
